import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  BookOpenIcon,
  CalendarIcon,
  ChatIcon,
  CogIcon,
  DesktopComputerIcon,
  GiftIcon,
  LogoutIcon,
  ShoppingCartIcon,
  SupportIcon,
  UserCircleIcon,
} from '@heroicons/react/outline';
import { useUser } from 'context/user-context';
import { isShipaidAdmin } from 'utils';
import { useSignOut } from '@nhost/react';
import { useNavigate } from 'react-router';
import { SelectStoreModal } from './SelectStoreModal';
import GeneralSearch from './GeneralSearch';
import classNames from 'classnames';
import { useStore } from 'context/store-context';
import { StoreSwitcher } from './StoreSwitcher';

type AppBarProps = {
  isSidebarCollapsed?: boolean;
  hasSidebar?: boolean;
  canSwitchStore?: boolean;
};

const AppBar = ({
  isSidebarCollapsed = false,
  hasSidebar = true,
  canSwitchStore = true,
}: AppBarProps): JSX.Element => {
  const navigate = useNavigate();
  const [isStoreModalOpen, setIsStoreModalOpen] = useState<null | boolean>(
    null,
  );
  const [profileOpen, setProfileOpen] = useState(false);
  const [supportOpen, setSupportOpen] = useState(false);

  const supportRef = useRef<HTMLDivElement | null>(null);
  const profileRef = useRef<HTMLDivElement | null>(null);

  const { signOut } = useSignOut(true);

  const { user } = useUser();
  const { storeId } = useStore();
  const isUserShipaidAdmin = isShipaidAdmin(user);

  const acccountMenuOptions = useMemo(
    () => [
      {
        key: 'account',
        to: '/settings/account',
        icon: CogIcon,
        label: 'Account',
      },
      {
        key: 'select-store-action',
        icon: ShoppingCartIcon,
        label: 'Select Store',
        action: () => {
          return setIsStoreModalOpen(true);
        },
      },
      {
        key: 'referral-program',
        to: '/referral-program',
        icon: GiftIcon,
        label: 'Refer & Earn',
      },
      {
        key: 'logout',
        label: 'Logout',
        icon: LogoutIcon,
        action: () => {
          signOut(true);
        },
      },
    ],
    [signOut],
  );

  const adminAcccountMenuOptions = useMemo(
    () => [
      {
        key: 'logout',
        label: 'Logout',
        icon: LogoutIcon,
        to: '',
        action: () => {
          signOut(true);
        },
      },
    ],
    [signOut],
  );

  const userActionMenus = useMemo(() => {
    if (isUserShipaidAdmin) return adminAcccountMenuOptions;
    return acccountMenuOptions;
  }, [isUserShipaidAdmin, adminAcccountMenuOptions, acccountMenuOptions]);

  const supportActionMenus = useMemo(() => {
    return [
      {
        key: 'knowledge-base-link',
        to: 'https://help.shipaid.com',
        icon: BookOpenIcon,
        label: 'Knowledge base',
        target: '_new',
      },
      {
        label: 'Chat with us',
        key: 'chat-with-us',
        icon: ChatIcon,
        action: () => {
          if (typeof window !== 'undefined' && window.$crisp) {
            window.$crisp.push(['do', 'chat:open']);
          } else {
            console.warn('Crisp chatbox script not yet loaded.');
          }
        },
      },
      {
        label: 'Schedule Support Call',
        to: 'https://calendly.com/shipaid-support/expert-call',
        key: 'schedule-support-call',
        icon: CalendarIcon,
        target: '_new',
      },
    ];
  }, []);

  useEffect(() => {
    function handleOutside(e: MouseEvent) {
      const target = e.target as Node;
      if (supportRef.current && !supportRef.current.contains(target)) {
        setSupportOpen(false);
      }
      if (profileRef.current && !profileRef.current.contains(target)) {
        setProfileOpen(false);
      }
    }

    function handleEsc(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        setSupportOpen(false);
        setProfileOpen(false);
      }
    }

    document.addEventListener('mousedown', handleOutside);
    document.addEventListener('keydown', handleEsc);
    return () => {
      document.removeEventListener('mousedown', handleOutside);
      document.removeEventListener('keydown', handleEsc);
    };
  }, []);

  return (
    <header
      className={classNames(
        'bg-[#262626] shadow-sm z-[150] transition-all duration-300 ease-in-out',
        hasSidebar ? (isSidebarCollapsed ? 'md:ml-16' : 'md:ml-72') : 'md:ml-0',
        'w-full md:w-auto',
      )}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div
          className={classNames(
            'items-center gap-3',
            isUserShipaidAdmin
              ? 'flex h-16 justify-end'
              : 'grid grid-cols-[minmax(0,1fr)_auto] py-3 lg:h-16 lg:grid-cols-[minmax(0,14rem)_minmax(0,1fr)_auto] lg:py-0',
          )}
        >
          {!isUserShipaidAdmin && (
            <>
              <StoreSwitcher canSwitchStore={canSwitchStore} />
              <div className="col-span-2 row-start-2 min-w-0 md:max-w-lg lg:col-span-1 lg:col-start-2 lg:row-start-1">
                <GeneralSearch key={storeId || 'no-store'} />
              </div>
            </>
          )}

          <div className="col-start-2 row-start-1 flex shrink-0 items-center space-x-2 lg:col-start-3">
            {!isUserShipaidAdmin && (
              <a
                href="https://resolve.shipaid.com/"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Open customer portal"
                title="Open customer portal"
                className="inline-flex items-center gap-2 rounded-md p-2 text-sm font-medium text-gray-200 hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-violet-400"
              >
                <DesktopComputerIcon className="h-5 w-5" aria-hidden="true" />
                <span className="hidden xl:inline">Customer portal</span>
              </a>
            )}
            {/* Notifications */}
            {!isUserShipaidAdmin && (
              <div className="relative" ref={supportRef}>
                <button
                  type="button"
                  aria-haspopup="true"
                  aria-expanded={supportOpen}
                  aria-controls="notifications-menu"
                  onClick={() => setSupportOpen((s) => !s)}
                  className="relative inline-flex items-center p-2 rounded-md text-gray-600 hover:bg-gray-100"
                >
                  <SupportIcon className="h-6 w-6" aria-hidden="true" />
                  <span className="sr-only">Open Support</span>
                </button>

                {supportOpen && (
                  <div
                    id="notifications-menu"
                    role="menu"
                    aria-orientation="vertical"
                    aria-labelledby="notifications-button"
                    className="origin-top-right absolute right-0 mt-2 w-56 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 focus:outline-none z-[100]"
                  >
                    <div className="py-1">
                      {supportActionMenus.map((menu) => {
                        const Icon = menu.icon;
                        return (
                          <button
                            key={menu.key}
                            onClick={() => {
                              if (menu.to) {
                                if (menu.target) {
                                  window.open(menu.to);
                                } else {
                                  navigate(menu.to);
                                }
                              } else {
                                menu.action && menu.action();
                              }
                              setSupportOpen(false);
                            }}
                            className="w-full flex items-center gap-2 text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                            role="menuitem"
                          >
                            <Icon className="w-6 h-6 mr-2" /> {menu.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Profile */}
            <div className="relative" ref={profileRef}>
              <button
                type="button"
                aria-haspopup="true"
                aria-expanded={profileOpen}
                aria-controls="profile-menu"
                onClick={() => setProfileOpen((s) => !s)}
                className="inline-flex items-center p-2 rounded-md text-gray-600 hover:bg-gray-100 "
              >
                <UserCircleIcon className="h-7 w-7" aria-hidden="true" />
                <span className="sr-only">Open profile menu</span>
              </button>

              {profileOpen && (
                <div
                  id="profile-menu"
                  role="menu"
                  aria-orientation="vertical"
                  aria-labelledby="profile-button"
                  className="origin-top-right w-[250px] absolute right-0 mt-2 w-40 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 focus:outline-none  z-[100]"
                >
                  {/* Store Header */}
                  <div className="p-4 border-b border-gray-200">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-pink-500 rounded-lg flex items-center justify-center text-gray-50 font-semibold">
                        {user?.displayName?.charAt(0).toUpperCase() || 'U'}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h2 className="font-semibold text-sm text-gray-500 truncate">
                          {user?.displayName || 'User'}
                        </h2>
                        <p className="text-xs text-gray-400 truncate">
                          {user?.defaultRole || 'ADMIN'}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="py-1">
                    {userActionMenus.map((menu) => {
                      const Icon = menu.icon;
                      return (
                        <button
                          key={menu.key}
                          onClick={() => {
                            if (menu.to) {
                              navigate(menu.to);
                            } else {
                              menu.action && menu.action();
                            }
                            setProfileOpen(false);
                          }}
                          className="w-full flex items-center gap-2 text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                          role="menuitem"
                        >
                          <Icon className="w-6 h-6 mr-2" /> {menu.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <SelectStoreModal
        isOpen={isStoreModalOpen}
        onClose={() => setIsStoreModalOpen(false)}
        addNewStores
      />
    </header>
  );
};

export default AppBar;
